import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

const StoreHomepage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const { data } = await axios.get(`${API}/api/store/products`);
                setProducts(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
    };

    return (
        <div className="min-h-screen bg-neutral-950 text-white font-sans overflow-hidden">
            {/* HERO SECTION */}
            <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 to-neutral-950/80 z-0" />
                <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="relative z-10 text-center px-4"
                >
                    <h1 className="text-6xl md:text-8xl font-black tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-500 to-purple-400 mb-6">
                        FUTURE OF FLIGHT
                    </h1>
                    <p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto mb-10">
                        Discover elite aerospace hardware, advanced flight controllers, and professional drones designed for the next generation of aviators.
                    </p>
                    <Link to="/store/products" className="inline-block bg-white text-black font-bold px-8 py-4 rounded-full tracking-wide hover:scale-105 transition-transform">
                        EXPLORE THE STORE
                    </Link>
                </motion.div>
                
                {/* Decorative glowing orb */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-600/20 rounded-full blur-[120px] -z-10 pointer-events-none" />
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="flex justify-between items-end mb-12">
                    <div>
                        <h2 className="text-4xl font-bold tracking-tight mb-2">Featured Gear</h2>
                        <div className="h-1 w-20 bg-purple-600 rounded"></div>
                    </div>
                    <Link to="/store/products" className="text-purple-400 hover:text-purple-300 transition-colors">
                        View all →
                    </Link>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-96 bg-neutral-900/50 rounded-2xl animate-pulse backdrop-blur-xl border border-neutral-800" />
                        ))}
                    </div>
                ) : products.length === 0 ? (
                    <div className="text-center py-32 border border-dashed border-neutral-800 rounded-3xl bg-neutral-900/20 backdrop-blur-md">
                        <div className="text-6xl mb-4">✨</div>
                        <h3 className="text-2xl font-bold mb-2">Store Opening Soon</h3>
                        <p className="text-neutral-400">We are curating the best hardware for you.</p>
                    </div>
                ) : (
                    <motion.div 
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        {products.slice(0, 3).map(product => (
                            <motion.div key={product._id} variants={itemVariants} className="group">
                                <Link to={`/store/products/${product.slug}`} className="block relative bg-neutral-900/40 backdrop-blur-xl border border-neutral-800 rounded-3xl overflow-hidden hover:border-purple-500/50 transition-colors duration-500">
                                    {/* Image Container */}
                                    <div className="aspect-[4/3] w-full overflow-hidden relative">
                                        <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 to-transparent z-10 opacity-60" />
                                        {product.images?.[0] ? (
                                            <img 
                                                src={product.images[0].imageUrl} 
                                                alt={product.name}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-in-out"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-neutral-800 flex items-center justify-center text-neutral-600">No Image</div>
                                        )}
                                        {product.status === 'out_of_stock' && (
                                            <div className="absolute top-4 left-4 z-20 bg-red-500/80 backdrop-blur text-white text-xs font-bold px-3 py-1 rounded-full">
                                                SOLD OUT
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Details */}
                                    <div className="p-6 relative z-20 -mt-10">
                                        <div className="bg-neutral-950/80 backdrop-blur-md border border-neutral-800 rounded-2xl p-5 shadow-2xl transform group-hover:-translate-y-2 transition-transform duration-500">
                                            <div className="text-xs text-purple-400 font-bold tracking-widest mb-2 uppercase">
                                                {product.category?.name || 'Hardware'}
                                            </div>
                                            <h3 className="text-xl font-bold mb-2 truncate">{product.name}</h3>
                                            <div className="flex justify-between items-center mt-4">
                                                <div className="text-xl font-mono">
                                                    {product.discountPrice ? (
                                                        <div className="flex flex-col">
                                                            <span className="text-sm text-neutral-500 line-through">₹{product.basePrice}</span>
                                                            <span className="text-white">₹{product.discountPrice}</span>
                                                        </div>
                                                    ) : (
                                                        <span>₹{product.basePrice}</span>
                                                    )}
                                                </div>
                                                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-purple-600 transition-colors">
                                                    <span className="text-white">→</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </section>
        </div>
    );
};

export default StoreHomepage;
